import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Heart, TrendingDown, TrendingUp, Database, CheckCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../store/authStore';
import { getSession, getReadings } from '../../api/sessions';
import { SkeletonBlock } from '../../components/SkeletonBlock';
type FilterRange = 'full' | 'firstHour' | 'lastHour' | 'custom';
export function SessionDetail() {
  const {
    sessionId
  } = useParams();
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const [session, setSession] = useState<any>(null);
  const [allReadings, setAllReadings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<FilterRange>('full');
  useEffect(() => {
    if (!token || !sessionId) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [sessionRes, readingsRes] = await Promise.all([getSession(token, sessionId), getReadings(token, sessionId, {
          limit: 2000
        })]);
        setSession(sessionRes);
        setAllReadings(readingsRes.readings || []);
      } catch (err) {
        // console.error('Failed to fetch session detail', err)
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, sessionId]);
  // Filter Readings
  const filteredReadings = useMemo(() => {
    if (filter === 'full' || allReadings.length === 0) return allReadings;
    const sorted = [...allReadings].sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());
    const firstTime = new Date(sorted[0].recordedAt).getTime();
    const lastTime = new Date(sorted[sorted.length - 1].recordedAt).getTime();
    if (filter === 'firstHour') {
      const oneHourLater = firstTime + 60 * 60 * 1000;
      return sorted.filter(r => new Date(r.recordedAt).getTime() <= oneHourLater);
    }
    if (filter === 'lastHour') {
      const oneHourBefore = lastTime - 60 * 60 * 1000;
      return sorted.filter(r => new Date(r.recordedAt).getTime() >= oneHourBefore);
    }
    return allReadings;
  }, [allReadings, filter]);
  // Split by sensor type
  const bpmReadings = useMemo(() => filteredReadings.filter(r => r.sensorType === 'bpm'), [filteredReadings]);
  const piezoReadings = useMemo(() => filteredReadings.filter(r => r.sensorType === 'sensor2'), [filteredReadings]);
  // Compute Metrics
  const metrics = useMemo(() => {
    if (bpmReadings.length === 0) return {
      avg: 0,
      min: 0,
      max: 0,
      minTime: '',
      maxTime: ''
    };
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    let minTime = '';
    let maxTime = '';
    bpmReadings.forEach(r => {
      const val = r.readingValue;
      sum += val;
      if (val < min) {
        min = val;
        minTime = r.recordedAt;
      }
      if (val > max) {
        max = val;
        maxTime = r.recordedAt;
      }
    });
    return {
      avg: Math.round(sum / bpmReadings.length),
      min,
      max,
      minTime: new Date(minTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      }),
      maxTime: new Date(maxTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    };
  }, [bpmReadings]);
  // Compute Notable Events
  const events = useMemo(() => {
    const result: any[] = [];
    if (bpmReadings.length === 0) return result;
    let currentState = 'normal'; // 'normal', 'high', 'low'
    const sorted = [...bpmReadings].sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());
    sorted.forEach(r => {
      const val = r.readingValue;
      const time = new Date(r.recordedAt).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
      if (val > 160 && currentState !== 'high') {
        result.push({
          type: 'high',
          value: val,
          time,
          desc: `BPM reached ${val} (above normal)`
        });
        currentState = 'high';
      } else if (val < 110 && currentState !== 'low') {
        result.push({
          type: 'low',
          value: val,
          time,
          desc: `BPM dropped to ${val} (below normal)`
        });
        currentState = 'low';
      } else if (val >= 110 && val <= 160 && currentState !== 'normal') {
        result.push({
          type: 'normal',
          value: val,
          time,
          desc: `BPM returned to normal range (${val})`
        });
        currentState = 'normal';
      }
    });
    return result;
  }, [bpmReadings]);
  // Formatters
  const formatTimeTick = (tickItem: any) => {
    return new Date(tickItem).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const CustomTooltip = ({
    active,
    payload,
    label,
    borderColor
  }: any) => {
    if (active && payload && payload.length) {
      return <div style={{
        background: 'white',
        border: 'none',
        borderRadius: '10px',
        boxShadow: '0 4px 16px rgba(107,33,232,0.2)',
        padding: '8px 12px',
        borderTop: `3px solid ${borderColor}`
      }} data-id="element-991">
          <p className="text-[12px] text-text-secondary mb-1" data-id="element-992">
            {formatTimeTick(label)}
          </p>
          <p className="text-[16px] font-bold text-text-primary" data-id="element-993">
            {payload[0].value}{' '}
            <span className="text-[12px] font-normal" data-id="element-994">val</span>
          </p>
        </div>;
    }
    return null;
  };
  const getStatusPill = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="bg-danger/15 text-danger px-3 py-1 rounded-pill text-[13px] font-medium" data-id="element-995">
            Active
          </span>;
      case 'incomplete':
        return <span className="bg-amber-500/15 text-amber-600 px-3 py-1 rounded-pill text-[13px] font-medium" data-id="element-996">
            Incomplete
          </span>;
      default:
        return <span className="bg-accent/20 text-accent px-3 py-1 rounded-pill text-[13px] font-medium" data-id="element-997">
            Completed
          </span>;
    }
  };
  const getBpmStatusLabel = (bpm: number) => {
    if (bpm < 110) return <span className="bg-amber-500/15 text-amber-600 px-2 py-0.5 rounded-pill text-[11px] font-medium" data-id="element-998">
          Low
        </span>;
    if (bpm > 160) return <span className="bg-danger/15 text-danger px-2 py-0.5 rounded-pill text-[11px] font-medium" data-id="element-999">
          High
        </span>;
    return <span className="bg-accent/20 text-accent px-2 py-0.5 rounded-pill text-[11px] font-medium" data-id="element-1000">
        Normal
      </span>;
  };
  const handleShare = () => {
    navigate(`/app/history/${sessionId}/share`, {
      state: {
        avgBpm: metrics.avg,
        minBpm: metrics.min,
        maxBpm: metrics.max,
        totalReadings: filteredReadings.length,
        sessionId
      }
    });
  };
  return <main className="w-full h-full flex flex-col pb-8" data-id="element-1001">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-1002">
        <div className="flex items-center" data-id="element-1003">
          <button onClick={() => navigate('/app/history')} className="mr-4 text-text-secondary hover:text-text-primary transition-colors" aria-label="Go back" data-id="element-1004">
            <ArrowLeft size={24} data-id="element-1005" />
          </button>
          <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-1006">
            Session Detail
          </h1>
        </div>
        <button onClick={handleShare} disabled={isLoading} className="flex items-center space-x-2 border border-primary text-primary px-4 py-2 rounded-pill text-[14px] font-medium hover:bg-primary/5 transition-colors disabled:opacity-50" data-id="element-1007">
          <Share2 size={16} data-id="element-1008" />
          <span data-id="element-1009">Share</span>
        </button>
      </div>

      <div className="flex flex-col xl:grid xl:grid-cols-[1fr_380px] xl:gap-8" data-id="element-1010">
        {/* LEFT COLUMN (Charts & Timeline) */}
        <div className="flex flex-col space-y-6 order-2 xl:order-1 mt-6 xl:mt-0" data-id="element-1011">
          {/* BPM Chart */}
          <div className="bg-white rounded-[20px] shadow-sm p-4 md:p-5" data-id="element-1012">
            <div className="flex justify-between items-center mb-2" data-id="element-1013">
              <h2 className="text-[16px] font-semibold text-text-primary" data-id="element-1014">
                Fetal Heart Rate
              </h2>
              <span className="text-[14px] text-text-secondary" data-id="element-1015">
                {filter === 'full' ? 'Full session' : filter === 'firstHour' ? 'First hour' : 'Last hour'}
              </span>
            </div>
            <div className="flex items-center mb-6" data-id="element-1016">
              <div className="w-[20px] h-[2px] bg-primary rounded-[1px] mr-2" data-id="element-1017" />
              <span className="text-[12px] text-text-secondary" data-id="element-1018">BPM</span>
            </div>

            {isLoading ? <SkeletonBlock height="240px" data-id="element-1019" /> : <div className="h-[240px] md:h-[300px] w-full" data-id="element-1020">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1021">
                  <LineChart data={bpmReadings} margin={{
                top: 5,
                right: 5,
                left: -20,
                bottom: 5
              }} data-id="element-1022">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9D5FF" vertical={false} data-id="element-1023" />
                    <XAxis dataKey="recordedAt" tickFormatter={formatTimeTick} stroke="#8A8A8A" fontSize={11} tickLine={false} axisLine={false} minTickGap={30} data-id="element-1024" />
                    <YAxis domain={[80, 180]} stroke="#8A8A8A" fontSize={11} tickLine={false} axisLine={false} data-id="element-1025" />
                    <Tooltip content={<CustomTooltip borderColor="#6B21E8" data-id="element-1027" />} data-id="element-1026" />
                    <Line type="monotone" dataKey="readingValue" stroke="#6B21E8" strokeWidth={2} dot={false} activeDot={{
                  r: 4,
                  fill: '#6B21E8',
                  strokeWidth: 0
                }} isAnimationActive={true} data-id="element-1028" />
                  </LineChart>
                </ResponsiveContainer>
              </div>}
          </div>

          {/* Uterine Activity Chart */}
          <div className="bg-white rounded-[20px] shadow-sm p-4 md:p-5" data-id="element-1029">
            <div className="flex justify-between items-center mb-2" data-id="element-1030">
              <h2 className="text-[16px] font-semibold text-text-primary" data-id="element-1031">
                Uterine Activity
              </h2>
            </div>
            <div className="flex items-center mb-6" data-id="element-1032">
              <div className="w-[20px] h-[2px] bg-accent rounded-[1px] mr-2" data-id="element-1033" />
              <span className="text-[12px] text-text-secondary" data-id="element-1034">Sensor 2</span>
            </div>

            {isLoading ? <SkeletonBlock height="240px" data-id="element-1035" /> : <div className="h-[240px] md:h-[300px] w-full" data-id="element-1036">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1037">
                  <LineChart data={piezoReadings} margin={{
                top: 5,
                right: 5,
                left: -20,
                bottom: 5
              }} data-id="element-1038">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9D5FF" vertical={false} data-id="element-1039" />
                    <XAxis dataKey="recordedAt" tickFormatter={formatTimeTick} stroke="#8A8A8A" fontSize={11} tickLine={false} axisLine={false} minTickGap={30} data-id="element-1040" />
                    <YAxis domain={[0, 4095]} stroke="#8A8A8A" fontSize={11} tickLine={false} axisLine={false} data-id="element-1041" />
                    <Tooltip content={<CustomTooltip borderColor="#00D4D4" data-id="element-1043" />} data-id="element-1042" />
                    <Line type="monotone" dataKey="readingValue" stroke="#00D4D4" strokeWidth={2} dot={false} activeDot={{
                  r: 4,
                  fill: '#00D4D4',
                  strokeWidth: 0
                }} isAnimationActive={true} data-id="element-1044" />
                  </LineChart>
                </ResponsiveContainer>
              </div>}
          </div>

          {/* Timeline */}
          <div className="pt-4" data-id="element-1045">
            <h2 className="text-[18px] font-semibold text-text-primary mb-4" data-id="element-1046">
              Notable Events
            </h2>

            {isLoading ? <SkeletonBlock height="100px" data-id="element-1047" /> : events.length === 0 ? <div className="bg-accent/10 border border-accent/20 rounded-[16px] p-4 flex items-start space-x-3" data-id="element-1048">
                <CheckCircle size={20} className="text-accent flex-shrink-0 mt-0.5" data-id="element-1049" />
                <p className="text-[14px] text-text-primary" data-id="element-1050">
                  No notable events — heart rate remained stable throughout this
                  session.
                </p>
              </div> : <div className="relative pl-4" data-id="element-1051">
                <div className="absolute left-[21px] top-2 bottom-2 w-[1px] bg-border" data-id="element-1052" />
                <div className="space-y-6" data-id="element-1053">
                  {events.map((ev, i) => <div key={i} className="relative flex items-start" data-id="element-1054">
                      <div className={`absolute left-0 w-[12px] h-[12px] rounded-full mt-1 z-10 ${ev.type === 'high' ? 'bg-danger' : ev.type === 'low' ? 'bg-amber-500' : 'bg-accent'}`} data-id="element-1055" />
                      <div className="ml-8" data-id="element-1056">
                        <span className="text-[13px] font-semibold text-text-primary block" data-id="element-1057">
                          {ev.time}
                        </span>
                        <span className="text-[13px] text-text-secondary" data-id="element-1058">
                          {ev.desc}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>

        {/* RIGHT COLUMN (Info & Metrics) */}
        <div className="flex flex-col space-y-6 order-1 xl:order-2" data-id="element-1059">
          {/* Session Info */}
          <div className="bg-white rounded-[20px] shadow-sm p-5" data-id="element-1060">
            {isLoading ? <SkeletonBlock height="120px" data-id="element-1061" /> : <>
                <div className="mb-4" data-id="element-1062">
                  <p className="text-[15px] font-semibold text-text-primary" data-id="element-1063">
                    {new Date(session?.startedAt || Date.now()).toLocaleDateString('en-US', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
                  </p>
                  <p className="text-[14px] text-text-secondary mt-1" data-id="element-1064">
                    {new Date(session?.startedAt || Date.now()).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                })}{' '}
                    —{' '}
                    {session?.endedAt ? new Date(session.endedAt).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                }) : 'Ongoing'}
                  </p>
                </div>
                <div className="mb-4" data-id="element-1065">
                  <p className="text-[14px] text-text-secondary" data-id="element-1066">Duration</p>
                  <p className="text-[15px] font-medium text-text-primary" data-id="element-1067">
                    {session?.duration ? `${Math.floor(session.duration / 60)} hours ${session.duration % 60} minutes` : '--'}
                  </p>
                </div>
                <div data-id="element-1068">
                  {getStatusPill(session?.sessionStatus || 'completed')}
                </div>
              </>}
          </div>

          {/* Filter Bar */}
          <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar space-x-2" data-id="element-1069">
            {[{
            id: 'full',
            label: 'Full session'
          }, {
            id: 'firstHour',
            label: 'First hour'
          }, {
            id: 'lastHour',
            label: 'Last hour'
          }].map(f => <button key={f.id} onClick={() => setFilter(f.id as FilterRange)} className={`flex-shrink-0 h-[32px] px-4 rounded-pill text-[13px] font-medium transition-colors duration-200 ${filter === f.id ? 'bg-primary text-white' : 'bg-border text-text-secondary hover:bg-border/80'}`} data-id="element-1070">
                {f.label}
              </button>)}
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-2 gap-4" data-id="element-1071">
            {/* Avg BPM */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-1072">
              <div className="w-[32px] h-[32px] bg-primary/10 rounded-full flex items-center justify-center mb-3" data-id="element-1073">
                <Heart size={16} className="text-primary" data-id="element-1074" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-1075" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-1076">
                  {metrics.avg || '--'}
                </span>}
              <span className="text-[12px] text-text-secondary mb-2" data-id="element-1077">
                Average BPM
              </span>
              {!isLoading && metrics.avg > 0 && getBpmStatusLabel(metrics.avg)}
            </div>

            {/* Min BPM */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-1078">
              <div className="w-[32px] h-[32px] bg-amber-500/15 rounded-full flex items-center justify-center mb-3" data-id="element-1079">
                <TrendingDown size={16} className="text-amber-600" data-id="element-1080" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-1081" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-1082">
                  {metrics.min === Infinity ? '--' : metrics.min}
                </span>}
              <span className="text-[12px] text-text-secondary mb-1" data-id="element-1083">
                Minimum BPM
              </span>
              <span className="text-[11px] text-text-secondary" data-id="element-1084">
                at {metrics.minTime || '--'}
              </span>
            </div>

            {/* Max BPM */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-1085">
              <div className="w-[32px] h-[32px] bg-accent/20 rounded-full flex items-center justify-center mb-3" data-id="element-1086">
                <TrendingUp size={16} className="text-accent" data-id="element-1087" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-1088" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-1089">
                  {metrics.max === -Infinity ? '--' : metrics.max}
                </span>}
              <span className="text-[12px] text-text-secondary mb-1" data-id="element-1090">
                Maximum BPM
              </span>
              <span className="text-[11px] text-text-secondary" data-id="element-1091">
                at {metrics.maxTime || '--'}
              </span>
            </div>

            {/* Total Readings */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-1092">
              <div className="w-[32px] h-[32px] bg-primary/10 rounded-full flex items-center justify-center mb-3" data-id="element-1093">
                <Database size={16} className="text-primary" data-id="element-1094" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-1095" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-1096">
                  {filteredReadings.length}
                </span>}
              <span className="text-[12px] text-text-secondary mb-1" data-id="element-1097">
                Data points
              </span>
              <span className="text-[11px] text-text-secondary" data-id="element-1098">
                {!isLoading && session?.duration ? `~${(filteredReadings.length / session.duration).toFixed(1)} per min` : '--'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-1099">{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </main>;
}